import React from 'react';
import ReactDOM from "react-dom";
import { classnames } from '../../../../utils/classnames';
import { ButtonGroup } from '../button/button';
import Icon from '../icon/icon';
import './modal.css';
import { iframeScrollTo } from '../../../../utils/scroll';

export default function Modal({ id, clasName = '', role = 'dialog', title = '', subtitle = '', open = false, onClose = null, submitButton = null, cancelButton = null, customButton = null, customFooter = null, children, ...rest }) {
	let previousActiveElement;

	const closeOnEscape = React.useCallback((e) => {
		if (e.key === "Escape" && onClose) { 
			onClose();
			previousActiveElement?.focus();
		}
	}, [onClose, previousActiveElement]);

	React.useEffect(() => {
		document.addEventListener('keyup', closeOnEscape);
		return () => {
			document.removeEventListener("keyup", closeOnEscape, true);
		};
	}, []);

	React.useEffect(() => {
        if (open) {
			previousActiveElement = document.activeElement;
			iframeScrollTo(document.querySelector('.neo-modal-container'));

			// // add all the elements inside modal which you want to make focusable
			const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

			const modal = document.getElementById(`modal_${id}`); // select the modal by it's id

			const firstFocusableElement = modal.querySelectorAll(focusableElements)[0]; // get first element to be focused inside modal
			const focusableContent = modal.querySelectorAll(focusableElements);
			const lastFocusableElement = focusableContent[focusableContent.length - 1]; // get last element to be focused inside modal

			function focusableModalElements(e) {
				let isTabPressed = e.key === 'Tab' || e.keyCode === 9;

				if (!isTabPressed) {
					return;
				}
				if (e.shiftKey) { // if shift key pressed for shift + tab combination
					if (document.activeElement.isEqualNode(firstFocusableElement)) {
						lastFocusableElement.focus(); // add focus for the last focusable element
						e.preventDefault();
					}
				} else { // if tab key is pressed
					if (document.activeElement.isEqualNode(lastFocusableElement)) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
						firstFocusableElement.focus(); // add focus for the first focusable element
						e.preventDefault();
					}
				}
			};

			document.addEventListener('keydown', focusableModalElements);
			firstFocusableElement.focus();

			return () => {
				document.removeEventListener("keydown", focusableModalElements, true);
			};
		}
    }, [open]);

	const describedby = React.useMemo(() =>
		[].concat(subtitle ? [`modal_${id}_subtitle`] : [])
			.concat(role === 'alertdialog' ? [`modal_${id}_content`] : [])
			.join(' ')
	, [subtitle, role, id]);

	if (!open) {
		return null;
	}

    return ReactDOM.createPortal(
		<div className='neo-modal-container'>
			<div
				role={role}
				id={`modal_${id}`}
				tabIndex={0}
				className={classnames('neo-modal', clasName)}
				aria-modal="true"
				{...(title && { 'aria-labelledby': `modal_${id}_label`})}
				{...(describedby && { 'aria-describedby': describedby })}
				{...rest}
			>
				{onClose && <div className='neo-modal-cross' onClick={onClose}>
                    <Icon name='cross' size='large'/>
                </div>}
				{title && <h2 id={`modal_${id}_label`} className='neo-modal-title'>{title}</h2>}
				{subtitle && (
					<p id={`modal_${id}_description`} className='neo-modal-subtitle'>{subtitle}</p>
				)}
				<div id={`modal_${id}_content`} className='neo-modal-content' tabIndex={-1}>{children}</div>
				{(submitButton || cancelButton || customButton || customFooter) && (
					<div className='neo-modal-footer'>
						{customFooter}
						<ButtonGroup>
							{cancelButton}
							{customButton}
							{submitButton}
						</ButtonGroup>
					</div>
				)}
			</div>
		</div>,
		window.document.body
	);
}